import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorMessageService {
  format(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const status = error.status || 'Unknown';
      const message = this.getServerMessage(error) || error.message;

      return `Status: ${status}. Message: ${message || 'No response message returned.'}`;
    }

    // Apollo Client 4 throws these instead of an HttpErrorResponse for
    // GraphQL mutation/query failures, so they need their own parsing to
    // surface the real backend message instead of falling to the generic one.
    if (CombinedGraphQLErrors.is(error) || CombinedProtocolErrors.is(error)) {
      const message = error.errors
        .map((e) => e?.message)
        .filter(Boolean)
        .join(' ');

      return `Status: GraphQL Error. Message: ${message || 'No response message returned.'}`;
    }

    if (ServerError.is(error) || ServerParseError.is(error)) {
      const message = this.getServerErrorBodyMessage(error.bodyText) || error.message;

      return `Status: ${error.statusCode || 'Unknown'}. Message: ${message || 'No response message returned.'}`;
    }

    return 'Status: Unknown. Message: Please try again.';
  }

  private getServerMessage(error: HttpErrorResponse): string {
    if (typeof error.error === 'string') {
      return error.error.trim();
    }

    if (!error.error || typeof error.error !== 'object') {
      return '';
    }

    return this.extractMessage(error.error as Record<string, unknown>);
  }

  private getServerErrorBodyMessage(bodyText: string): string {
    if (!bodyText) {
      return '';
    }

    try {
      const parsed = JSON.parse(bodyText);
      if (parsed && typeof parsed === 'object') {
        return this.extractMessage(parsed as Record<string, unknown>);
      }
    } catch {
      // Not JSON — fall through and use the raw body text below.
    }

    return bodyText.trim();
  }

  private extractMessage(response: Record<string, unknown>): string {
    const message =
      response['detail'] ?? response['message'] ?? response['title'];

    return typeof message === 'string' ? message.trim() : JSON.stringify(response);
  }
}